/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import {
  ParticipantEvent,
  RoomEvent,
  Track,
  type Participant,
  type Room,
} from "livekit-client";

export type HealthWarning = "poor" | "lost" | "media" | "uplink" | null;
const RECOVERY_MS = 20000;
class ParticipantHealth {
  warning: HealthWarning = null;
  private media: HealthWarning = null;
  private recovery?: ReturnType<typeof setTimeout>;
  private listeners = new Set<() => void>();
  constructor(private participant: Participant) {
    participant.on(ParticipantEvent.ConnectionQualityChanged, this.update);
    this.update();
  }
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  snapshot = (): HealthWarning => this.warning;
  private publish(value: HealthWarning): void {
    if (value === this.warning) return;
    this.warning = value;
    this.listeners.forEach((listener) => listener());
  }
  update = (): void => {
    const quality = this.participant.connectionQuality;
    const bad =
      quality === "lost" ? "lost" : quality === "poor" ? "poor" : this.media;
    if (bad) {
      clearTimeout(this.recovery);
      this.recovery = undefined;
      this.publish(bad);
    } else if (quality !== "good" && quality !== "excellent") {
      clearTimeout(this.recovery);
      this.recovery = undefined;
    } else if (this.warning && !this.recovery) {
      this.recovery = setTimeout(() => {
        this.recovery = undefined;
        this.publish(null);
      }, RECOVERY_MS);
    }
  };
  sample(warning: HealthWarning): void {
    this.media = warning;
    this.update();
  }
  suspend(): void {
    clearTimeout(this.recovery);
    this.recovery = undefined;
  }
}
const health = new WeakMap<Participant, ParticipantHealth>();
export function participantHealth(participant: Participant): ParticipantHealth {
  let value = health.get(participant);
  if (!value) {
    value = new ParticipantHealth(participant);
    health.set(participant, value);
  }
  return value;
}

type Sample = Record<string, any>;
/** Compare counters within one RTP stream, never total lifetime loss or bitrate alone. */
export function rtpImpairment(
  current: Sample,
  previous?: Sample,
  camera = true,
): boolean {
  if (
    !previous ||
    current.timestamp <= previous.timestamp ||
    current.timestamp - previous.timestamp > 10000
  )
    return false;
  const received = current.packetsReceived - previous.packetsReceived;
  const lost = current.packetsLost - previous.packetsLost;
  const loss =
    received >= 0 &&
    lost > 0 &&
    received + lost >= 20 &&
    lost / (received + lost) >= 0.05;
  const frozen =
    camera &&
    current.type === "inbound-rtp" &&
    (current.freezeCount > previous.freezeCount ||
      current.totalFreezesDuration > previous.totalFreezesDuration);
  const jitter =
    (current.kind === "audio" || current.mediaType === "audio") &&
    received > 0 &&
    current.jitter >= 0.12;
  return (
    loss ||
    frozen ||
    jitter ||
    (current.type === "remote-inbound-rtp" &&
      (current.fractionLost >= 0.05 || current.roundTripTime >= 1))
  );
}

/** One sampler per call, shared by all camera and spotlight tiles. No media is uploaded. */
export function monitorRoomHealth(room: Room): () => void {
  let stopped = false,
    busy = false;
  let previous = new WeakMap<object, Map<string, Sample>>();
  let streaks = new WeakMap<object, number>();
  let progress = new WeakMap<object, Map<string, number>>();
  const entries = new Set<ParticipantHealth>();
  const sample = async (): Promise<void> => {
    if (stopped || busy) return;
    if (document.hidden) {
      entries.forEach((entry) => entry.suspend());
      return;
    }
    busy = true;
    try {
      const participants = [
        room.localParticipant,
        ...room.remoteParticipants.values(),
      ];
      await Promise.all(
        participants.map(async (participant) => {
          const entry = participantHealth(participant);
          entries.add(entry);
          let bad: HealthWarning = null;
          let supported = false;
          let activeTracks = 0;
          for (const publication of participant.trackPublications.values()) {
            const track = publication.track;
            if (
              !track ||
              publication.isMuted ||
              !publication.isEnabled ||
              (!participant.isLocal && !publication.isSubscribed) ||
              track.mediaStreamTrack.readyState !== "live" ||
              (participant.isLocal && track.mediaStreamTrack.muted) ||
              ("isUpstreamPaused" in track && track.isUpstreamPaused) ||
              track.streamState === "paused"
            ) {
              if (track) {
                previous.delete(track);
                streaks.delete(track);
                progress.delete(track);
              }
              continue;
            }
            try {
              const report = await track.getRTCStatsReport();
              if (stopped) return;
              if (!report) continue;
              supported = true;
              const before = previous.get(track) ?? new Map();
              const after = new Map<string, Sample>();
              const advances = progress.get(track) ?? new Map<string, number>();
              let impaired = false,
                interrupted = false,
                bandwidth = false;
              report.forEach((stat) => {
                if (
                  ![
                    "inbound-rtp",
                    "outbound-rtp",
                    "remote-inbound-rtp",
                  ].includes(stat.type)
                )
                  return;
                after.set(stat.id, stat);
                const old = before.get(stat.id);
                if (
                  !participant.isLocal &&
                  publication.source === Track.Source.Camera &&
                  stat.type === "inbound-rtp" &&
                  typeof stat.framesReceived === "number"
                ) {
                  if (
                    !old ||
                    stat.framesReceived !== old.framesReceived ||
                    stat.timestamp - old.timestamp > 10000
                  )
                    advances.set(stat.id, stat.timestamp);
                  const lastAdvance = advances.get(stat.id) ?? stat.timestamp;
                  // This reports interrupted media, not a claim about whose network failed.
                  interrupted ||=
                    stat.framesReceived > 0 &&
                    stat.timestamp - lastAdvance >= 6000;
                }
                interrupted ||=
                  publication.source === Track.Source.Camera &&
                  stat.type === "inbound-rtp" &&
                  !!old &&
                  stat.timestamp > old.timestamp &&
                  stat.timestamp - old.timestamp <= 10000 &&
                  (stat.freezeCount > old.freezeCount ||
                    stat.totalFreezesDuration > old.totalFreezesDuration);
                impaired ||= rtpImpairment(
                  stat,
                  before.get(stat.id),
                  publication.source === Track.Source.Camera,
                );
                bandwidth ||=
                  participant.isLocal &&
                  stat.type === "outbound-rtp" &&
                  stat.qualityLimitationReason === "bandwidth";
              });
              previous.set(track, after);
              progress.set(track, advances);
              const streak =
                impaired || bandwidth ? (streaks.get(track) ?? 0) + 1 : 0;
              streaks.set(track, streak);
              if (streak >= 2 || interrupted)
                bad = participant.isLocal ? "uplink" : "media";
            } catch {
              /* Unsupported or replaced tracks fall back to server quality. */
            }
          }
          if (!stopped) {
            // Muting/removing all tracks is not itself a network failure.
            if (supported || activeTracks === 0) entry.sample(bad);
            else entry.update();
          }
        }),
      );
    } finally {
      busy = false;
    }
  };
  const tick = (): void => {
    void sample();
  };
  const visibility = (): void => {
    previous = new WeakMap();
    streaks = new WeakMap();
    progress = new WeakMap();
    entries.forEach((entry) => entry.suspend());
    tick();
  };
  tick();
  const interval = setInterval(tick, 2000);
  room.on(RoomEvent.ParticipantConnected, tick);
  document.addEventListener("visibilitychange", visibility);
  return () => {
    stopped = true;
    clearInterval(interval);
    room.off(RoomEvent.ParticipantConnected, tick);
    document.removeEventListener("visibilitychange", visibility);
    entries.forEach((entry) => entry.suspend());
  };
}
